import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { assertPresent, isPresent } from '@luminovo/commons';
import {
    CenteredLayout,
    CircularProgress,
    colorSystem,
    Flexbox,
    NonIdealState,
    PrimaryButton,
} from '@luminovo/design-system';
import { TableBody, TableCell, TableRow } from '@mui/material';
import { shallow, useSelector } from '@tanstack/react-store';
import { CircleAlertIcon, InboxIcon } from 'lucide-react';
import React from 'react';
import { TableComponents } from 'react-virtuoso';

import { FilterChips, TableSlot } from './TanStackMenuBar';
import { Table } from './tanstackTypes';
import { TanStackTableContext } from './type';

export const DefaultOuterEmptyPlaceholder: TableComponents<unknown, TanStackTableContext<any>>['EmptyPlaceholder'] = ({
    context,
}) => {
    const { table, EmptyPlaceholder = DefaultInnerEmptyPlaceholder, isLoading } = assertPresent(context);
    const { isLoadingError, onRetry } = assertPresent(table.options.meta);
    const { globalFilter, columnFilters } = useSelector(
        table.store,
        (state) => ({ globalFilter: state.globalFilter, columnFilters: state.columnFilters }),
        { compare: shallow },
    );

    const hasGlobalFilter = Boolean(globalFilter);
    // Only count filters whose column actually exists on this table; stale filters from sibling
    // tabs don't affect the row model, so they shouldn't trigger the "No items match" empty state.
    const hasColumnFilters = columnFilters.some((f) => isPresent(table.getColumn(f.id)));

    const handleClearFilters = React.useCallback(() => {
        table.resetGlobalFilter();
        table.resetColumnFilters(true);
    }, [table]);

    return (
        <TableBody>
            <TableRow style={{ borderColor: colorSystem.neutral.white }}>
                <TableCell
                    colSpan={table.getAllLeafColumns().filter((c) => c.getIsVisible()).length}
                    style={{ backgroundColor: colorSystem.neutral.white }}
                >
                    <CenteredLayout
                        style={{
                            height: '100%',
                            minHeight: '200px',
                            position: 'sticky',
                            maxWidth: '100vw',
                            left: 8,
                        }}
                    >
                        {isLoadingError ? (
                            <TableLoadError onRetry={onRetry} />
                        ) : isLoading ? (
                            <CircularProgress />
                        ) : hasGlobalFilter ? (
                            <NoMatchesWithGlobalFilter
                                globalFilter={globalFilter}
                                handleClearFilters={handleClearFilters}
                            />
                        ) : hasColumnFilters ? (
                            <NoMatchWithColumnFilters table={table} handleClearFilters={handleClearFilters} />
                        ) : (
                            <TableSlot
                                Slot={EmptyPlaceholder}
                                table={table}
                                sharedContext={table.options.meta?.sharedContext}
                            />
                        )}
                    </CenteredLayout>
                </TableCell>
            </TableRow>
        </TableBody>
    );
};

/** What a table shows when its query failed; also for views that draw the table's rows another way, like a chart. */
export const TableLoadError: React.FunctionComponent<{ onRetry: (() => void) | undefined }> = ({ onRetry }) => (
    <NonIdealState
        variant="error"
        icon={<CircleAlertIcon />}
        title={t`Could not load the data`}
        description={t`The data didn't load. Try again in a moment.`}
        action={onRetry && { children: t`Try again`, onClick: () => onRetry() }}
    />
);

const NoMatchesWithGlobalFilter: React.FunctionComponent<{ globalFilter: string; handleClearFilters: () => void }> = ({
    globalFilter,
    handleClearFilters,
}) => (
    <NonIdealState
        title={t`No match`}
        description={<Trans>No exact matches for "{globalFilter}"</Trans>}
        action={{
            onClick: handleClearFilters,
            children: t`Clear filters`,
        }}
    />
);

const NoMatchWithColumnFilters: React.FunctionComponent<{
    handleClearFilters: () => void;
    table: Table<any>;
}> = ({ handleClearFilters, table }) => {
    return (
        <NonIdealState
            title={t`No items match your filters`}
            description={t`Try adjusting or clearing your filters to see more results.`}
            style={{ maxWidth: '600px' }}
            action={
                <Flexbox
                    gap={'12px'}
                    sx={{
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                    }}
                >
                    <FilterChips table={table} />
                    <span>
                        <PrimaryButton size="small" onClick={handleClearFilters}>
                            {t`Clear filters`}
                        </PrimaryButton>
                    </span>
                </Flexbox>
            }
        />
    );
};

const DefaultInnerEmptyPlaceholder: React.FunctionComponent = () => (
    <NonIdealState icon={<InboxIcon />} title={t`It's empty here`} description={t`We have no data to display yet.`} />
);
